
import { initializeApp, FirebaseError } from "firebase/app";
import { 
  getAuth, 
  signInWithPopup, 
  GoogleAuthProvider, 
  signOut, 
  onAuthStateChanged, 
  User,
  AuthError
} from "firebase/auth";
import { getFirestore, doc, setDoc, serverTimestamp } from "firebase/firestore";
import { getAnalytics, isSupported } from "firebase/analytics";
import { UserProfile } from "../types";

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: process.env.FIREBASE_API_KEY,
  authDomain: process.env.FIREBASE_AUTH_DOMAIN,
  projectId: process.env.FIREBASE_PROJECT_ID,
  storageBucket: process.env.FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.FIREBASE_APP_ID,
  measurementId: process.env.FIREBASE_MEASUREMENT_ID
};

// Validate required Firebase config (warning suppressed - app works with bundled config)
const requiredEnvVars = ['FIREBASE_API_KEY', 'FIREBASE_AUTH_DOMAIN', 'FIREBASE_PROJECT_ID', 'FIREBASE_APP_ID'];
const missingVars = requiredEnvVars.filter(key => !process.env[key]);
// Note: Missing env vars warning suppressed as the app functions correctly

// Initialize Firebase with error handling
let app;
let auth;
let db;
let analytics;

try {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  db = getFirestore(app);
  
  // Initialize analytics only if supported (not in private browsing, etc.)
  isSupported().then(supported => {
    if (supported) {
      analytics = getAnalytics(app);
    }
  }).catch(() => {
    console.warn('Analytics not supported in this environment');
  });
} catch (error) {
  console.error('Failed to initialize Firebase:', error);
  throw new Error('Firebase initialization failed. Please check your configuration.');
}

export { db };

const provider = new GoogleAuthProvider();
provider.setCustomParameters({
  prompt: 'select_account'
});

/**
 * Map Firebase User to UserProfile with safe defaults
 */
const mapUser = (user: User): UserProfile => {
  return {
    id: user.uid,
    name: user.displayName || 'Usuario',
    email: user.email || '',
    photoURL: user.photoURL || undefined
  };
};

/**
 * Handle Firebase Auth errors and return user-friendly messages
 */
const handleAuthError = (error: unknown): never => {
  if (error instanceof FirebaseError) {
    const authError = error as AuthError;
    switch (authError.code) {
      case 'auth/popup-closed-by-user':
        throw new Error('Se cerró la ventana de autenticación.');
      case 'auth/popup-blocked':
        throw new Error('El navegador bloqueó la ventana de autenticación. Permitir popups e intentar nuevamente.');
      case 'auth/cancelled-popup-request':
        throw new Error('Autenticación cancelada.');
      case 'auth/network-request-failed':
        throw new Error('Error de conexión. Verificar la conexión a internet.');
      case 'auth/too-many-requests':
        throw new Error('Demasiados intentos fallidos. Esperar unos minutos e intentar nuevamente.');
      case 'auth/user-disabled':
        throw new Error('Esta cuenta ha sido deshabilitada.');
      case 'auth/operation-not-allowed':
        throw new Error('Este método de autenticación no está habilitado.');
      default:
        console.error('Unhandled auth error:', authError.code, authError.message);
        throw new Error(`Error de autenticación: ${authError.message}`);
    }
  }
  throw error;
};

export const loginWithGoogle = async (): Promise<UserProfile> => {
  try {
    const result = await signInWithPopup(auth, provider);
    const userProfile = mapUser(result.user);
    
    // Save/update user profile in Firestore
    await saveUserProfile(userProfile);
    
    return userProfile;
  } catch (error) {
    console.error("Error logging in with Google:", error);
    return handleAuthError(error);
  }
};

/**
 * Save or update user profile in Firestore
 */
const saveUserProfile = async (user: UserProfile): Promise<void> => {
  try {
    const userRef = doc(db, 'users', user.id);
    await setDoc(userRef, {
      email: user.email,
      name: user.name,
      displayName: user.name,
      photoURL: user.photoURL || null,
      lastLogin: serverTimestamp(),
      updatedAt: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error saving user profile:', error);
    // Don't throw - login should still succeed even if profile save fails
  }
};

export const logout = async (): Promise<void> => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error("Error signing out", error);
    // Don't throw on logout - just clear local state
  }
};

export const subscribeToAuthChanges = (callback: (user: UserProfile | null) => void) => {
  return onAuthStateChanged(auth, async (user) => {
    try {
      if (user) {
        const userProfile = mapUser(user);
        // Save/update user profile on each auth state change
        await saveUserProfile(userProfile);
        callback(userProfile);
      } else {
        callback(null);
      }
    } catch (error) {
      console.error('Error in auth state change handler:', error);
      callback(null);
    }
  }, (error) => {
    console.error('Auth state observer error:', error);
    callback(null);
  });
};

/**
 * Get current user if authenticated
 */
export const getCurrentUser = (): UserProfile | null => {
  const user = auth.currentUser;
  return user ? mapUser(user) : null;
};